/**
 * Subscription API Service
 *
 * Provides typed API calls for subscription management endpoints.
 * Authenticated calls go through apiClient; the public plans list uses plain
 * axios so hard-refresh on /pricing cannot fail when MSAL is still initializing.
 */

import axios from 'axios';
import apiClient from './apiClient';
import type {
  SubscriptionPlan,
  SubscriptionStatus,
  CheckoutSessionResult,
  CreateCheckoutRequest,
  ContactSelectionRequest,
  SubscriptionCampaign,
} from '../types/subscription';

/**
 * Gets the API base URL from environment or default to relative path.
 */
const getApiBaseUrl = (): string => import.meta.env.VITE_API_URL || '/api';

/**
 * Get all active subscription plans (public AllowAnonymous endpoint).
 * Uses plain axios — not apiClient — so the request does not wait on / fail
 * when MSAL is uninitialized during a hard refresh of a public page.
 */
export const getSubscriptionPlans = async (): Promise<SubscriptionPlan[]> => {
  const response = await axios.get<SubscriptionPlan[]>(`${getApiBaseUrl()}/subscription/plans`);
  return response.data;
};

/**
 * Get public display data for a subscription campaign (intro offer pricing).
 * Returns null when the slug is unknown (404).
 */
export const getSubscriptionCampaign = async (
  slug: string,
): Promise<SubscriptionCampaign | null> => {
  try {
    const response = await apiClient.get<SubscriptionCampaign>(`/subscription/campaigns/${slug}`);
    return response.data;
  } catch (error: unknown) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw error;
  }
};

/**
 * Get the current user's subscription status.
 *
 * @param forceRefresh When true, the backend bypasses its cache and sync throttle
 *   to read live data from Stripe. Used on the account page so users always see
 *   the current subscription state (e.g. after cancelling via customer service).
 */
export const getSubscriptionStatus = async (
  forceRefresh = false,
): Promise<SubscriptionStatus> => {
  const params = forceRefresh ? { forceRefresh: true } : undefined;
  const response = await apiClient.get<SubscriptionStatus>('/subscription/status', { params });
  return response.data;
};

/**
 * Create a checkout session for subscribing to a plan.
 * In mock mode, returns mockActivated=true (subscription auto-activated).
 * In real mode, returns a clientSecret for Stripe Embedded Checkout.
 */
export const createCheckoutSession = async (
  request: CreateCheckoutRequest,
): Promise<CheckoutSessionResult> => {
  const response = await apiClient.post<CheckoutSessionResult>('/subscription/checkout', request);
  return response.data;
};

/**
 * Verify a completed Stripe Checkout Session and activate the subscription.
 * Webhook fallback: if the webhook is delayed, this directly confirms with Stripe
 * and creates the local subscription record.
 */
export const verifyCheckoutSession = async (sessionId: string): Promise<boolean> => {
  const response = await apiClient.post<{ activated: boolean }>('/subscription/verify-checkout', {
    sessionId,
  });
  return response.data.activated;
};

/**
 * Create a portal session for managing an existing subscription.
 * Returns a URL to redirect to.
 */
export const createPortalSession = async (): Promise<string> => {
  const response = await apiClient.post<{ url: string }>('/subscription/portal');
  return response.data.url;
};

/**
 * Submit contact selection (lock contacts not in the keep list).
 */
export const submitContactSelection = async (
  request: ContactSelectionRequest,
): Promise<void> => {
  await apiClient.post('/contacts/lock-selection', request);
};

/**
 * Complete the first-login onboarding flow.
 */
export const completeOnboarding = async (): Promise<void> => {
  await apiClient.post('/users/complete-onboarding');
};
